import React from "react";
import { useNavigate } from "react-router-dom";
import { IconManager } from "../../components/core/IconManager/IconManager";
import { IconSizeEnum } from "../../types/AppTypes";
import { ReactComponent as BackArrowIcon } from "../../icons/arrowLeft.svg";
import {
  StyledPrivacyPolicy,
  StyledPage,
  StyledDocHead,
  StyledSectionTitle,
  StyledSubTitle,
  StyledMinorTitle,
  StyledParagraph,
  StyledList,
  StyledLink,
  StyledTable,
  StyledTableWrapper,
  StyledSplitCell,
  StyledCallout,
} from "./PrivacyPolicy.styled";

const Callout = ({ children }: { children: React.ReactNode }) => (
  <StyledCallout>
    <span className="label">Detto semplicemente</span>
    {children}
  </StyledCallout>
);

const MailLink = ({ address }: { address: string }) => (
  <StyledLink href={`mailto:${address}`}>{address}</StyledLink>
);

const SectionLink = ({
  target,
  children,
}: {
  target: string;
  children: React.ReactNode;
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document
      .getElementById(target)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <StyledLink href={`#${target}`} onClick={handleClick}>
      {children}
    </StyledLink>
  );
};

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <StyledPrivacyPolicy>
      <IconManager size={IconSizeEnum.LARGE} onClick={() => navigate(-1)}>
        <BackArrowIcon />
      </IconManager>

      <StyledPage>
        <StyledDocHead>
          <h1>MyCareer</h1>
          <div className="sub">Informativa Privacy - Abstract S.r.l.</div>
        </StyledDocHead>

        <StyledTableWrapper>
          <StyledTable className="meta-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Date</th>
                <th>Creation</th>
                <th>Abstract approval</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1.0</td>
                <td>Draft</td>
                <td>10/07/26</td>
                <td>Angelica Baglio</td>
                <td>Matteo Pompilio (DPO)</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        {/* ============ INFORMATIVA BREVE ============ */}
        <StyledSectionTitle>Informativa Breve</StyledSectionTitle>
        <StyledParagraph>
          {
            "L'informativa che segue è una versione ridotta dell'informativa estesa che puoi trovare "
          }
          <StyledLink
            href="https://idoqsrl.sharepoint.com/sites/HR-Amministrazione/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FHR%2DAmministrazione%2FShared%20Documents%2FAbstract%5FInformativa%20dipendenti%5Fver%2E01%2D24%2Epdf&parent=%2Fsites%2FHR%2DAmministrazione%2FShared%20Documents"
            target="_blank"
            rel="noopener noreferrer"
          >
            qui
          </StyledLink>
          {", nonché una estensione della più generale informativa dipendenti."}
        </StyledParagraph>

        <StyledSubTitle>1.1 Chi è responsabile dei tuoi dati</StyledSubTitle>
        <StyledParagraph>
          {
            "Abstract agisce come data controller ed è l'unico responsabile per garantire il corretto trattamento dei tuoi dati personali nel contesto della presente piattaforma."
          }
        </StyledParagraph>

        <StyledSubTitle>1.2 Quali dati e da dove vengono</StyledSubTitle>
        <StyledParagraph>
          {
            <>
              Trattiamo dati che ci arrivano dalla piattaforma{" "}
              <strong>Zucchetti</strong> (nome, cognome, codice fiscale, data di
              assunzione, inquadramento, struttura organizzativa) e dati che
              potrai inserire tu o il tuo Career Manager direttamente in
              piattaforma (valutazioni, obiettivi, soft skills, commenti, note,
              percorso di carriera, formazione). Tratteremo anche dati sull'uso
              dell'applicazione (pagine visitate e interazioni, con
              mascheramento automatico dei contenuti), per migliorarne il
              funzionamento: trovi i dettagli nella sezione dedicata
              dell'informativa completa.
            </>
          }
        </StyledParagraph>

        <StyledSubTitle>
          1.3 Per quale motivo trattiamo questi dati
        </StyledSubTitle>
        <StyledParagraph>
          {
            "Per gestire il tuo rapporto di lavoro e il processo di Career Management, pianificare la tua formazione, documentare le decisioni di carriera, garantire la sicurezza della piattaforma e, se necessario, esercitare i nostri diritti legittimi."
          }
        </StyledParagraph>

        <StyledSubTitle>
          1.4 Per quanto tempo trattiamo i tuoi dati
        </StyledSubTitle>
        <StyledParagraph>
          {
            "I dati anagrafici e di valutazione vengono conservati per non oltre dieci anni dalla cessazione del rapporto di lavoro o dalla chiusura del ciclo CM, salvo sospensione in caso di contenzioso. Maggiori informazioni su criteri e tempi sono presenti nell'informativa estesa."
          }
        </StyledParagraph>

        <StyledSubTitle>1.5 Quali sono i tuoi diritti</StyledSubTitle>
        <StyledParagraph>
          {
            "Hai il diritto di accedere ai tuoi dati, correggerli, chiederne la cancellazione, opporti al trattamento e riceverne copia. Puoi esercitare questi diritti scrivendo alla mail "
          }
          <MailLink address="privacy@abstract.it" />
          {" oppure al nostro DPO a "}
          <MailLink address="dpo@abstract.it" />
          {"."}
        </StyledParagraph>

        {/* ============ INFORMATIVA ESTESA ============ */}
        <StyledSectionTitle>Informativa estesa</StyledSectionTitle>

        <StyledSubTitle>
          1. Titolare del trattamento e responsabile protezione dati personali
        </StyledSubTitle>
        <StyledList>
          <li>
            <strong>Titolare del trattamento:</strong> Abstract Srl
          </li>
          <li>
            <strong>Sede Legale:</strong> Foro Buonaparte, 70, Milano – 20121
          </li>
          <li>
            <strong>Mail di contatto:</strong>{" "}
            <MailLink address="privacy@abstract.it" />
          </li>
          <li>
            <strong>DPO – Data protection officer:</strong>{" "}
            <MailLink address="dpo@abstract.it" />
          </li>
        </StyledList>
        <Callout>
          {
            "Il tuo datore di lavoro, Abstract Srl, è l'unico responsabile per la gestione dei tuoi dati personali in myCareer. Il DPO è invece una figura indipendente, che assicura l'applicazione dei principi previsti dalla normativa di riferimento per tutelare i tuoi dati. Puoi contattarlo liberamente nel caso avessi dubbi o per esercitare i tuoi diritti (senza passare da HR o dal tuo manager)."
          }
        </Callout>

        <StyledSubTitle>2. A chi è rivolta questa informativa</StyledSubTitle>
        <StyledParagraph>
          {
            "La presente informativa è destinata agli utenti della piattaforma MyCareer, i quali possono accedere assumendo due ruoli:"
          }
        </StyledParagraph>
        <StyledList>
          <li>
            <strong>Soggetto valutato (Dipendente):</strong>{" "}
            {
              "sei il soggetto al quale si riferiscono i dati di anagrafica, performance, soft skills, formazione e percorso professionale trattati nella piattaforma."
            }
          </li>
          <li>
            <strong>Valutatore</strong>{" "}
            {
              "(Career manager, Competence center leader, Practice Leader, HR manager): se assumi questi ruoli, tratti i dati personali dei collaboratori che ti vengono assegnati, per conto e su istruzioni del Titolare."
            }
          </li>
        </StyledList>
        <StyledParagraph>
          {
            "I due ruoli (valutato e valutatore) possono anche coesistere nella stessa persona."
          }
        </StyledParagraph>

        <StyledSubTitle>
          3. Quali dati trattiamo e da dove provengono
        </StyledSubTitle>

        <StyledMinorTitle id="sezione-3-1">
          3.1 Dati che riceviamo da altre piattaforme aziendali
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "Una parte dei dati che ti riguardano sono estrapolati da altre piattaforme aziendali. Nella tabella che segue sono riportati i dati che vengono trasmessi automaticamente, ogni giorno, dal sistema aziendale di gestione paghe."
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Dati personali</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Dati anagrafici</td>
                <td>Nome, cognome, codice fiscale</td>
              </tr>
              <tr>
                <td>Contatti</td>
                <td>Mail aziendale</td>
              </tr>
              <tr>
                <td>Dati contrattuali</td>
                <td>Data assunzione (e data cessazione) rapporto</td>
              </tr>
              <tr>
                <td>Dati organizzativi/aziendali</td>
                <td>Internal grade, practice, centro di competenza</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledMinorTitle id="sezione-3-2">
          3.2 Dati inseriti dal dipendente/collaboratore
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "In altri casi, la Piattaforma richiede al collaboratore l'inserimento diretto di alcune informazioni."
          }
        </StyledParagraph>
        <StyledMinorTitle>3.2.1 Commenti e chat</StyledMinorTitle>
        <StyledParagraph>
          {
            "Il dipendente/collaboratore può inserire dati personali nei campi a testo libero o nello scambio di messaggi con il proprio CM."
          }
        </StyledParagraph>
        <StyledMinorTitle>3.2.2 Formazione professionale</StyledMinorTitle>
        <StyledParagraph>
          {
            "La piattaforma, inoltre, consente al collaboratore di valorizzare il proprio profilo, inserendo informazioni relative alla propria storia professionale, alle competenze maturate e al percorso formativo. Si tratta di dati che il collaboratore inserisce volontariamente e che può modificare o cancellare in qualsiasi momento. I dati si riferiscono a pregresse esperienze professionali, competenze acquisite e formazione (titoli di studio, certificazioni, corsi completati e percorsi formativi). Tale segmento può includere anche il link a profili social professionali (es. LinkedIn), inserito volontariamente e rimovibile in qualsiasi momento. Questa tipologia di dato, quando fornito, è trattato esclusivamente ai fini della gestione del percorso professionale e non viene condiviso con terzi senza il consenso dell'interessato."
          }
        </StyledParagraph>
        <Callout>
          {
            "Puoi arricchire il tuo profilo in MyCareer inserendo direttamente le tue esperienze lavorative, le tue competenze e la tua formazione. Questi dati li controlli tu: puoi modificarli (o cancellarli) in ogni momento. Ricorda che la permanenza del dato in piattaforma è soggetta ad una fase approvativa sulla correttezza dell'informazione."
          }
        </Callout>

        <StyledMinorTitle id="sezione-3-3">
          3.3 Dati inseriti dai Career Manager
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "In altri casi, i dati sono inseriti dall'utente che assume il ruolo di Career manager, Competence center leader, Practice Leader, HR manager. Questa tipologia di utenza inserisce in piattaforma dati concernenti i collaboratori di cui presiede i percorsi di crescita in azienda. Si riporta una tabella sinottica sul tema."
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Chi inserisce</th>
                <th>Dati specifici</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Soft skills comportamentali</td>
                <td>
                  Career Manager / Practice Leader / Centro di competenza Leader
                </td>
                <td>
                  Dimensione comportamentale e livello assegnato (es. gestione
                  dello stress, adattabilità, comunicazione)
                </td>
              </tr>
              <tr>
                <td>Obiettivi e performance</td>
                <td>Career Manager</td>
                <td>Descrizione obiettivi, percentuale di raggiungimento</td>
              </tr>
              <tr>
                <td>Valutazione finale</td>
                <td>Career Manager / HR</td>
                <td>
                  Posizionamento proposto internamente e posizionamento finale
                  comunicato
                </td>
              </tr>
              <tr>
                <td>Commento HR e Admin</td>
                <td>HR / Admin</td>
                <td>Valutazioni testuali libere sulla scheda CM</td>
              </tr>
              <tr>
                <td>Percorso professionale</td>
                <td>
                  Career Manager / Practice Leader / Centro di competenza Leader
                </td>
                <td>
                  Piano d&apos;azione, obiettivi di sviluppo, job family, grade,
                  path, subgrade
                </td>
              </tr>
              <tr>
                <td>Formazione assegnata</td>
                <td>Career Manager / HR</td>
                <td>
                  Corsi assegnati, certificazioni, date di conseguimento e
                  scadenza, stato
                </td>
              </tr>
              <tr>
                <td>Sviluppo organizzativo</td>
                <td>HR / Practice Leader</td>
                <td>Valutazione risorse organizzative e piano di sviluppo</td>
              </tr>
              <tr>
                <td>Assegnazione CM</td>
                <td>HR / Admin</td>
                <td>
                  Identità del tuo Career Manager, date di assegnazione e cambio
                </td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledMinorTitle>3.4 Proposta di posizionamento</StyledMinorTitle>
        <StyledParagraph>
          {
            "Il campo “posizionamento proposto” contiene la valutazione, formulata internamente dal Career Manager, preliminare alla fase di valutazione. Questo dato non viene mostrato in piattaforma in quanto è parte del processo deliberativo interno. Hai tuttavia diritto di richiederne l'accesso nelle forme indicate nella sezione specifica di questo documento; fermo restando quanto previsto dall'art. 15(4) GDPR in caso di impatto sui diritti e libertà di altri soggetti."
          }
        </StyledParagraph>

        <StyledMinorTitle>
          3.5 Fase di valutazione annuale o calibration
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "Il processo di valutazione è interamente tracciabile. La proposta di posizionamento formulata, come nel paragrafo che precede, viene sottoposta a revisione e poi confermata da un utente autorizzato. Il dato di conferma viene conservato a garanzia della trasparenza del processo decisionale e per assicurare la non automazione del processo."
          }
        </StyledParagraph>
        <Callout>
          {
            "Qui si trovano i dati che elabora il Career Manager su di te durante l'anno e per l'intero processo di valutazione. I dati riguardano te, seppur siano stati scritti da un altro utente. Pertanto, hai sempre il diritto di conoscerli e, in alcuni casi, di chiederne la correzione. Solo per alcuni campi (ad es. proposta interna del CM), l'azienda potrebbe non mostrarti integralmente il campo per evitare di esporre informazioni riservate o dati personali di terzi."
          }
        </Callout>

        <StyledSubTitle>4. Finalità e basi giuridiche</StyledSubTitle>
        <StyledParagraph>
          {
            "La normativa prevede che ogni operazione sui dati personali (cd. trattamento) abbia una finalità precisa e una base giuridica specifica. Di seguito l'elenco completo."
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Finalità</th>
                <th>Categorie dati</th>
                <th>Base giuridica</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Gestione del rapporto di lavoro (censimento risorse,
                  anagrafica, struttura organizzativa)
                </td>
                <td>
                  Sezione <SectionLink target="sezione-3-1">3.1</SectionLink>
                </td>
                <td rowSpan={4}>
                  Art. 6(1)(b) – esecuzione del contratto (di lavoro)
                </td>
              </tr>
              <tr>
                <td>Valutazione performance per erogazione premi risultato</td>
                <td>
                  Sezione <SectionLink target="sezione-3-2">3.2</SectionLink> e{" "}
                  <SectionLink target="sezione-3-3">3.3</SectionLink>
                </td>
              </tr>
              <tr>
                <td>
                  Pianificazione della formazione e sviluppo professionale
                </td>
                <td>
                  Dati su formazione, certificazioni, percorso di carriera
                </td>
              </tr>
              <tr>
                <td>Invio di comunicazioni relative al ciclo CM (notifiche)</td>
                <td>E-mail aziendale, contenuto delle notifiche</td>
              </tr>
              <tr>
                <td>Valutazione performance per esigenze organizzative</td>
                <td>
                  Sezione <SectionLink target="sezione-3-2">3.2</SectionLink> e{" "}
                  <SectionLink target="sezione-3-3">3.3</SectionLink>
                </td>
                <td rowSpan={5}>Art. 6(1)(f) – legittimo interesse</td>
              </tr>
              <tr>
                <td>Difesa in giudizio di Abstract</td>
                <td>Dati di valutazione e anagrafica</td>
              </tr>
              <tr>
                <td>Reportistica e analisi HR aggregate</td>
                <td>Dati aggregati e/o nominativi del ciclo CM</td>
              </tr>
              <tr>
                <td>
                  Sicurezza del sistema, tracciabilità degli accessi e audit
                  tecnico
                </td>
                <td>Log di sistema</td>
              </tr>
              <tr>
                <td>Condivisione CV con clienti</td>
                <td>Tutti i dati di carriera professionale (CV)</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledSubTitle>5. Profilazione</StyledSubTitle>
        <StyledParagraph>
          {
            "Il GDPR definisce la profilazione come qualsiasi forma di trattamento automatizzato di dati personali che valuti, analizzi o preveda aspetti della tua persona; in particolare relativi al rendimento professionale, alla situazione economica, alle preferenze, agli interessi, all'affidabilità o al comportamento (art. 4(4) GDPR). MyCareer svolge attività che rientrano in questa definizione. Di seguito le descriviamo in modo trasparente."
          }
        </StyledParagraph>

        <StyledMinorTitle>
          5.1 Le attività di profilazione in MyCareer
        </StyledMinorTitle>
        <StyledMinorTitle>5.1.1 Career Program</StyledMinorTitle>
        <StyledParagraph>
          {
            "Nel corso del ciclo di Career Management annuale, ci sono delle finestre di valutazioni nelle quali vengono utilizzati dei dati, anche aggregati, attraverso i quali vengono definiti dei criteri di salary review e di performance management."
          }
        </StyledParagraph>
        <StyledMinorTitle>
          5.1.2 Valutazione sistematica delle soft skills
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "Le competenze comportamentali vengono valutate anno per anno e aggregate nel tempo. Il confronto tra cicli successivi produce un profilo evolutivo delle tue competenze trasversali. Anche questa attività costituisce profilazione ai sensi dell'art. 4(4). La base giuridica, come già definito nella tabella che precede (cfr. Pianificazione della formazione e sviluppo professionale), è l'esecuzione del contratto di lavoro (art. 6(1)(b) GDPR)."
          }
        </StyledParagraph>
        <StyledMinorTitle>
          5.1.3 Profilazione e decisioni automatizzate: cosa non facciamo
        </StyledMinorTitle>
        <StyledParagraph>
          {
            <>
              L'art. 22 GDPR tutela le persone dal rischio di essere sottoposte
              a decisioni basate esclusivamente su trattamenti automatizzati che
              producano effetti giuridici o incidano in modo significativo sulla
              loro sfera personale.{" "}
              <strong>
                In MyCareer nessuna decisione viene adottata in modo
                esclusivamente automatizzato.
              </strong>{" "}
              La valutazione è formulata dal Career Manager e sottoposto a un
              processo di valutazione che coinvolge più figure aziendali.
              L'esito finale viene confermato manualmente da un utente
              autorizzato, il cui intervento è tracciato nella piattaforma. Le
              decisioni di carriera (promozioni, percorsi di sviluppo,
              assegnazioni) sono sempre adottate da persone fisiche, sulla base
              dei dati disponibili in piattaforma, non dalla piattaforma stessa.{" "}
              <strong>
                Riteniamo pertanto che il trattamento non ricada nell'ambito di
                applicazione dell'art. 22 GDPR.
              </strong>{" "}
              In ogni caso, ti garantiamo le tutele previste per i trattamenti
              assistiti da algoritmi: l'intervento umano effettivo e
              attribuibile a un soggetto identificato, il diritto di ottenere la
              spiegazione della logica del posizionamento e il diritto di
              contestarne l'esito rivolgendoti a HR o al DPO.
            </>
          }
        </StyledParagraph>
        <StyledMinorTitle>5.1.4 Le tue garanzie</StyledMinorTitle>
        <StyledParagraph>
          {
            "Hai diritto di conoscere la logica del processo di valutazione e le conseguenze previste (art. 13(2)(f) GDPR). In sintesi: il processo non produce automaticamente alcun effetto contrattuale. È uno strumento di pianificazione interna che orienta le conversazioni di sviluppo tra te e il tuo Career Manager. Non è vincolante, non è definitivo e viene aggiornato ad ogni ciclo annuale."
          }
        </StyledParagraph>
        <StyledParagraph>
          {
            "I singoli dati trattati nella piattaforma (valutazioni, soft skills, etc.) sono analizzati nel loro contesto specifico e non vengono combinati con fonti esterne (es. dati di navigazione, comportamenti extralavorativi) per produrre profili ulteriori. La profilazione effettuata in MyCareer è circoscritta alle finalità dichiarate e non può essere riutilizzata per finalità incompatibili senza una distinta base giuridica (art. 5(1)(b) e art. 6(4) GDPR)."
          }
        </StyledParagraph>
        <StyledParagraph>
          {
            "Hai inoltre il diritto di accedere ai dati che ti riguardano (art. 15 GDPR), inclusi i risultati della profilazione. Ti informiamo tuttavia che il campo posizionamento proposto (ossia la valutazione preliminare formulata dal Career Manager prima della Fase di Calibrazione) è soggetto alla limitazione prevista dall'art. 15(4) GDPR: la sua comunicazione potrebbe essere differita o limitata qualora pregiudichi i diritti e le libertà di altri soggetti coinvolti nel processo."
          }
        </StyledParagraph>
        <Callout>
          {
            "MyCareer elabora valutazioni professionali su di te (la tua performance, le tue competenze e la tua crescita) e le combina in una classificazione complessiva. Questo si chiama “profilazione” e vogliamo che tu lo sappia. Allo stesso tempo, però, nessun sistema automatico prende decisioni sulla tua carriera: ogni classificazione passa attraverso la revisione di persone fisiche e viene confermata manualmente. Hai il diritto di sapere quali dati abbiamo su di te e come vengono usati."
          }
        </Callout>

        <StyledSubTitle>6. Periodi di conservazione</StyledSubTitle>
        <StyledParagraph>
          {
            "I dati personali vengono conservati entro un certo termine. Allo scadere del termine i dati vengono cancellati o anonimizzati."
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Categoria di dati</th>
                <th>Termine decorrenza</th>
                <th>Conservazione</th>
                <th>Criterio</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Dati anagrafici e contrattuali</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Credenziali di accesso (MicrosoftId, users)</td>
                <td>Disattivazione account aziendale</td>
                <td>Contestuale alla dismissione</td>
                <td>Art. 5(1)(e) GDPR</td>
              </tr>
              <tr>
                <td>Scheda CM (valutazione, obiettivi e percorso)</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Soft skills comportamentali</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Commenti</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Dati di valutazione (calibration)</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.; art. 22 GDPR</td>
              </tr>
              <tr>
                <td>Note nel processo CM</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Assegnazioni Career Manager</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c. / art. 2948(1)(5) c.c.</td>
              </tr>
              <tr>
                <td>Dati Valutazione Career Program</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 6(1)(f) GDPR; art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Percorso formativo individuale (formazione volontaria)</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 2946 c.c.</td>
              </tr>
              <tr>
                <td>Sviluppo organizzativo</td>
                <td>Cessazione del rapporto di lavoro</td>
                <td>10 anni</td>
                <td>Art. 6(1)(f)</td>
              </tr>
              <tr>
                <td>Log notifiche e-mail</td>
                <td>Conferma di invio</td>
                <td>12 mesi</td>
                <td>Art. 5(1)(e) GDPR</td>
              </tr>
              <tr>
                <td>Log notifiche in-app</td>
                <td>Conferma di lettura</td>
                <td>6 mesi</td>
                <td>Art. 5(1)(e) GDPR</td>
              </tr>
              <tr>
                <td>Log di sistema</td>
                <td>Registrazione evento</td>
                <td>Da 6 a 12 mesi</td>
                <td>Art. 32 GDPR; art. 5(2) GDPR</td>
              </tr>
              <tr>
                <td>Registrazioni analytics (session recording)</td>
                <td>Registrazione</td>
                <td>90 giorni</td>
                <td>Art. 5(1)(e) GDPR</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
        <StyledParagraph>
          <em>Nota di coordinamento:</em>
          {
            " per i dati valutativi legati al ciclo di Career Management (scheda CM, soft skills, valutazione finale e calibrazione, note del processo) il termine di conservazione decorre dalla chiusura del ciclo CM dell'anno di riferimento, se anteriore alla cessazione del rapporto di lavoro."
          }
        </StyledParagraph>

        <StyledMinorTitle>6.1 Revisione periodica</StyledMinorTitle>
        <StyledParagraph>
          {
            "I periodi di conservazione sono soggetti a revisione periodica. L'esito della revisione è documentato a cura del Titolare del trattamento (Abstract). Qualora i dati debbano essere conservati oltre i termini indicati in ragione di un contenzioso in corso o imminente, si applica la sospensione dei termini di cancellazione ai sensi dell'art. 17(3)(e) GDPR, documentata e limitata alla durata del procedimento."
          }
        </StyledParagraph>
        <Callout>
          {
            "La maggior parte dei dati che ti riguardano viene mantenuta per 10 anni dal momento in cui hai lasciato l'azienda o dopo la chiusura del ciclo di valutazione. Questo perché la legge prevede che eventuali cause di lavoro possano essere avviate fino a 10 anni dopo. Quando il termine scade, i dati vengono cancellati o resi anonimi."
          }
        </Callout>

        <StyledSubTitle>7. Con chi condividiamo i tuoi dati?</StyledSubTitle>
        <StyledMinorTitle>
          7.1 Responsabili del trattamento (art. 28 GDPR)
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "I tuoi dati non vengono ceduti a terzi né utilizzati per finalità commerciali. Vengono tuttavia trattati, per conto del Titolare e sulla base di specifici accordi, dai seguenti soggetti:"
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Soggetto</th>
                <th>Finalità</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Zucchetti S.p.A.</td>
                <td>
                  Gestione software paghe; generazione file di esportazione dati
                  verso MyCareer
                </td>
              </tr>
              <tr>
                <td>Microsoft Corporation (Azure)</td>
                <td>
                  Hosting infrastruttura cloud: Azure SQL Server, Static Web
                  App, Azure Databricks, Azure Active Directory
                </td>
              </tr>
              <tr>
                <td>SendGrid</td>
                <td>Invio delle notifiche e-mail generate dalla piattaforma</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledMinorTitle>7.2 Soggetti interni autorizzati</StyledMinorTitle>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Ruolo</th>
                <th>Perimetro di accesso</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Admin / HR</td>
                <td>
                  Visibilità completa su tutti i collaboratori e tutte le schede
                  CM
                </td>
              </tr>
              <tr>
                <td>Area Leader</td>
                <td>
                  Visibilità sui collaboratori delle Practice di propria
                  competenza
                </td>
              </tr>
              <tr>
                <td>Practice Leader</td>
                <td>Visibilità sui collaboratori della propria Practice</td>
              </tr>
              <tr>
                <td>CC Manager / CC Leader</td>
                <td>
                  Visibilità sui collaboratori del proprio Centro di Competenza
                </td>
              </tr>
              <tr>
                <td>Career Manager</td>
                <td>Visibilità limitata ai collaboratori assegnati</td>
              </tr>
              <tr>
                <td>Collaboratore</td>
                <td>
                  Visibilità esclusiva sul proprio profilo (scheda CM propria,
                  se abilitata)
                </td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledMinorTitle>7.3 Esportazione dati</StyledMinorTitle>
        <StyledParagraph>
          {
            "Gli utenti con ruolo Admin/HR possono esportare in formato tabellare i dati aggregati (inclusi i posizionamenti individuali). L'export è tracciato nei log di sistema e accessibile solo a personale autorizzato. I dati esportati rimangono soggetti agli obblighi di riservatezza e non possono essere utilizzati per finalità incompatibili con quelle qui dichiarate."
          }
        </StyledParagraph>

        <StyledMinorTitle>
          7.3.1 Analisi dell&apos;utilizzo della piattaforma (analytics)
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "La piattaforma potrà utilizzare uno strumento di analisi dell'utilizzo (product/web analytics) per finalità di miglioramento dell'esperienza d'uso e di correzione degli errori tecnici (base giuridica: art. 6(1)(f) GDPR – legittimo interesse del Titolare). Lo strumento potrà raccogliere: pagine visitate, interazioni (click, scroll), mappe di calore e registrazioni di sessione con mascheramento automatico dei campi e dei contenuti a schermo (i contenuti mascherati non lasciano il tuo dispositivo), con indirizzo IP anonimizzato e senza utilizzo di identificativi diretti. Le registrazioni sono conservate per un massimo di 90 giorni con cancellazione automatica e sono accessibili solo a personale autorizzato, per finalità esclusivamente tecniche: non vengono utilizzate per valutare la tua prestazione lavorativa né per finalità disciplinari."
          }
        </StyledParagraph>

        <StyledSubTitle>8. I tuoi diritti</StyledSubTitle>
        <StyledParagraph>
          {
            "Al fine di tutelare i tuoi dati personali, garantiamo un sistema che supporta l'esercizio dei diritti previsti dalla normativa. I diritti da te esercitabili sono riportati di seguito."
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Diritto</th>
                <th>Art. GDPR</th>
                <th>Contenuto</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Accesso</td>
                <td>Art. 15</td>
                <td>
                  Ottenere conferma del trattamento e copia dei dati che ti
                  riguardano
                </td>
              </tr>
              <tr>
                <td>Rettifica</td>
                <td style={{ padding: 0 }}>
                  <StyledSplitCell>
                    <div className="split-part">Art. 16</div>
                    <div className="split-part">
                      Limitazione al diritto di rettifica
                    </div>
                  </StyledSplitCell>
                </td>
                <td style={{ padding: 0 }}>
                  <StyledSplitCell>
                    <div className="split-part">
                      Ottenere la correzione di dati inesatti o
                      l&apos;integrazione di dati incompleti
                    </div>
                    <div className="split-part">
                      {
                        "Il diritto di rettifica (art. 16 GDPR) non si applica a giudizi valutativi del datore di lavoro nell'ambito del processo di valutazione della performance, né può essere esercitato per modificare retroattivamente dati che documentano decisioni già adottate. Resta ferma la facoltà dell'interessato di presentare osservazioni scritte che saranno archiviate nel fascicolo personale."
                      }
                    </div>
                  </StyledSplitCell>
                </td>
              </tr>
              <tr>
                <td>Cancellazione</td>
                <td>Art. 17</td>
                <td>
                  {
                    "Ottenere la cancellazione dei dati. Il diritto di cancellazione non si applica quando il trattamento è necessario per l'accertamento, l'esercizio o la difesa di un diritto in sede giudiziaria (art. 17(3)(b) GDPR). In tali circostanze il Titolare comunica all'interessato il motivo del differimento."
                  }
                </td>
              </tr>
              <tr>
                <td>Limitazione</td>
                <td>Art. 18</td>
                <td>
                  Ottenere la sospensione temporanea del trattamento (es.
                  durante una verifica di esattezza)
                </td>
              </tr>
              <tr>
                <td>Portabilità</td>
                <td>Art. 20</td>
                <td>Ricevere in formato strutturato i dati che hai fornito</td>
              </tr>
              <tr>
                <td>Opposizione</td>
                <td>Art. 21</td>
                <td>Opporti al trattamento basato su legittimo interesse</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>

        <StyledMinorTitle>
          8.1 Procedura per l&apos;esercizio dei diritti
        </StyledMinorTitle>
        <StyledParagraph>
          {
            "Al fine di esercitare i diritti su elencati, puoi contattare il titolare o il DPO designato ai recapiti di seguito:"
          }
        </StyledParagraph>
        <StyledList>
          <li>
            {"E-mail: "}
            <MailLink address="privacy@abstract.it" />
            {" – "}
            <MailLink address="dpo@abstract.it" />
          </li>
        </StyledList>
        <StyledParagraph>
          {
            <>
              Il Titolare si impegna a rispondere entro{" "}
              <strong>30 giorni</strong> dalla ricezione, prorogabili a{" "}
              <strong>90 giorni</strong> in caso di complessità, con
              comunicazione del motivo della proroga entro i primi 30 giorni
              (art. 12(3) GDPR). La risposta è sempre gratuita.
            </>
          }
        </StyledParagraph>
        <Callout>
          {
            "Hai diversi diritti sui tuoi dati personali. Quello più usato è il diritto di accesso: puoi chiedere all'azienda di mostrarti tutto quello che ha su di te. Puoi anche chiedere di correggere dati sbagliati, di cancellarli (anche se in alcuni casi l'azienda non può farlo subito per motivi legali), o di non usarli per certi scopi."
          }
        </Callout>

        <StyledMinorTitle>
          8.2 Reclamo presso l&apos;Autorità di controllo
        </StyledMinorTitle>
        <StyledParagraph>
          {
            <>
              Se ritieni che il trattamento dei tuoi dati personali violi il
              GDPR, hai il diritto di proporre reclamo all'
              <strong>
                Autorità Garante per la protezione dei dati personali
              </strong>{" "}
              (Garante Privacy), senza pregiudizio per il ricorso
              giurisdizionale:
            </>
          }
        </StyledParagraph>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Canale</th>
                <th>Riferimento</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Sito web</td>
                <td>
                  <StyledLink href="https://www.garanteprivacy.it">
                    www.garanteprivacy.it
                  </StyledLink>
                </td>
              </tr>
              <tr>
                <td>E-mail</td>
                <td>
                  <MailLink address="garante@gpdp.it" />
                </td>
              </tr>
              <tr>
                <td>PEC</td>
                <td>
                  <MailLink address="protocollo@pec.gpdp.it" />
                </td>
              </tr>
              <tr>
                <td>Indirizzo</td>
                <td>Piazza Venezia 11, 00187 Roma</td>
              </tr>
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
        <Callout>
          {
            "Se pensi che l'azienda stia usando i tuoi dati in modo sbagliato e non ottieni risposta soddisfacente, puoi fare un reclamo al Garante Privacy, l'autorità pubblica italiana che vigila sul rispetto delle norme sulla privacy. È un tuo diritto e puoi farlo liberamente."
          }
        </Callout>

        <StyledSubTitle>9. Aggiornamenti</StyledSubTitle>
        <StyledParagraph>
          {
            "Il Titolare si riserva di aggiornare la presente informativa in caso di variazioni nei trattamenti descritti, nelle norme applicabili o nell'architettura della piattaforma. Le modifiche sostanziali saranno comunicate agli interessati tramite notifica nella piattaforma MyCareer con congruo preavviso e, ove richiesto, con richiesta di nuova presa visione."
          }
        </StyledParagraph>
      </StyledPage>
    </StyledPrivacyPolicy>
  );
};
